if (!customElements.get('team-directory-section')) {
  customElements.define(
    'team-directory-section',
    class TeamDirectorySection extends HTMLElement {
      constructor() {
        super();

        this.activeCell = null;
        this.activeTrigger = null;
        this.detailShell = document.createElement('div');
        this.detailShell.className = 'team-directory__detail-shell';
        this.detailShell.hidden = true;
        this.isAnimating = false;
        this.motionFrame = null;
        this.motionFallback = null;
        this.scrollFrame = null;
        this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onClick = this.onClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        this.detailHome = this.querySelector('[data-team-detail-home]');
        this.detailScienceHome = this.querySelector('[data-team-detail-science]');
        this.mobileBreakpoint = Number(this.dataset.mobileBreakpoint || 749);
        this.addEventListener('click', this.onClick);
        document.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('resize', this.onResize);
        this.cancelScheduledMotion();
      }

      onClick(event) {
        if (this.isAnimating) return;

        const closeButton = event.target.closest('[data-team-close]');
        if (closeButton) {
          event.preventDefault();
          this.closeDetail();
          return;
        }

        const trigger = event.target.closest('[data-member-trigger]');
        if (!trigger || !this.contains(trigger)) return;

        const cell = trigger.closest('[data-member-cell]');
        if (!cell) return;

        event.preventDefault();

        if (this.activeCell === cell) {
          this.closeDetail();
          return;
        }

        this.openDetail(cell, trigger);
      }

      onKeyDown(event) {
        if (event.key === 'Escape' && this.activeCell && !this.isAnimating) {
          this.closeDetail();
        }
      }

      onResize() {
        if (!this.activeCell || !this.activeTrigger) return;
        this.placeDetail(this.activeCell);
        this.detailShell.style.height = 'auto';
      }

      openDetail(cell, trigger) {
        const template = cell.querySelector('[data-member-template]');
        if (!template) return;

        if (this.activeCell) {
          this.switchDetail(cell, trigger, template);
          return;
        }

        this.isAnimating = true;
        this.cancelScheduledMotion();
        this.clearDetailClasses();
        this.detailShell.innerHTML = template.innerHTML;
        this.detailShell.hidden = false;
        this.detailShell.style.height = '0px';

        this.setActiveMember(cell, trigger);

        this.placeDetail(cell);

        this.motionFrame = requestAnimationFrame(() => {
          const targetHeight = this.detailShell.scrollHeight;
          this.detailShell.classList.add('is-entering');
          this.detailShell.style.height = `${targetHeight}px`;

          this.waitForShellTransition(() => {
            this.detailShell.style.height = 'auto';
            this.detailShell.classList.remove('is-entering');
            this.isAnimating = false;
          });

          this.scrollDetailIntoView();
        });
      }

      switchDetail(cell, trigger, template) {
        this.isAnimating = true;
        this.cancelScheduledMotion();

        const currentHeight = this.detailShell.getBoundingClientRect().height || this.detailShell.scrollHeight;
        this.detailShell.style.height = `${currentHeight}px`;
        this.clearDetailClasses();
        this.detailShell.classList.add('is-switching-out');
        this.setActiveMember(cell, trigger);

        this.waitForCardAnimation(() => {
          this.detailShell.classList.remove('is-switching-out');
          this.detailShell.innerHTML = template.innerHTML;
          this.placeDetail(cell);
          this.detailShell.style.height = `${currentHeight}px`;

          this.motionFrame = requestAnimationFrame(() => {
            const targetHeight = this.detailShell.scrollHeight;
            this.detailShell.classList.add('is-switching-in');
            this.detailShell.style.height = `${targetHeight}px`;

            this.waitForShellTransition(() => {
              this.detailShell.style.height = 'auto';
              this.detailShell.classList.remove('is-switching-in');
              this.isAnimating = false;
            });

            this.scrollDetailIntoView();
          });
        });
      }

      placeDetail(cell) {
        const grid = cell.closest('[data-team-grid]');
        if (!grid) return;

        const isScience = grid.dataset.teamGrid === 'science';

        if (isScience && this.detailScienceHome) {
          this.detailScienceHome.insertAdjacentElement('afterend', this.detailShell);
        } else if (this.detailHome) {
          this.detailHome.insertAdjacentElement('afterend', this.detailShell);
        }
      }

      closeDetail() {
        if (!this.activeCell) return;

        const returnFocus = this.activeTrigger;

        this.clearActiveMember();
        this.isAnimating = true;
        this.cancelScheduledMotion();
        this.scrollToTrigger(returnFocus);

        const currentHeight = this.detailShell.getBoundingClientRect().height || this.detailShell.scrollHeight;
        this.clearDetailClasses();
        this.detailShell.style.height = `${currentHeight}px`;
        this.detailShell.offsetHeight;
        this.detailShell.classList.add('is-exiting');

        this.motionFrame = requestAnimationFrame(() => {
          this.detailShell.style.height = '0px';

          this.waitForShellTransition(() => {
            this.detailShell.style.height = '';
            this.detailShell.innerHTML = '';
            this.detailShell.hidden = true;
            this.isAnimating = false;

            if (this.detailHome) {
              this.detailHome.insertAdjacentElement('afterend', this.detailShell);
            }

            this.detailShell.classList.remove('is-exiting');
          });
        });

        if (returnFocus) {
          returnFocus.focus({ preventScroll: true });
        }
      }

      setActiveMember(cell, trigger) {
        if (this.activeTrigger) {
          this.activeTrigger.setAttribute('aria-expanded', 'false');
        }
        if (this.activeCell) {
          this.activeCell.classList.remove('is-active');
        }

        this.activeCell = cell;
        this.activeTrigger = trigger;
        this.activeCell.classList.add('is-active');
        this.activeTrigger.setAttribute('aria-expanded', 'true');
      }

      clearActiveMember() {
        if (this.activeTrigger) {
          this.activeTrigger.setAttribute('aria-expanded', 'false');
        }
        if (this.activeCell) {
          this.activeCell.classList.remove('is-active');
        }

        this.activeTrigger = null;
        this.activeCell = null;
      }

      clearDetailClasses() {
        this.detailShell.classList.remove('is-entering', 'is-exiting', 'is-switching-in', 'is-switching-out');
      }

      cancelScheduledMotion() {
        if (this.motionFrame) {
          cancelAnimationFrame(this.motionFrame);
          this.motionFrame = null;
        }
        if (this.scrollFrame) {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = null;
        }
        if (this.motionFallback) {
          clearTimeout(this.motionFallback);
          this.motionFallback = null;
        }
      }

      waitForShellTransition(callback) {
        if (this.prefersReducedMotion()) {
          callback();
          return;
        }

        let hasRun = false;
        const onEnd = (event) => {
          if (event.target !== this.detailShell || event.propertyName !== 'height') return;
          done();
        };
        const done = () => {
          if (hasRun) return;
          hasRun = true;
          this.detailShell.removeEventListener('transitionend', onEnd);
          if (this.motionFallback) {
            clearTimeout(this.motionFallback);
            this.motionFallback = null;
          }
          callback();
        };

        this.detailShell.addEventListener('transitionend', onEnd);
        this.motionFallback = setTimeout(done, this.getMotionDuration() + 120);
      }

      waitForCardAnimation(callback) {
        const card = this.detailShell.querySelector('.team-directory__detail-card');

        if (!card || this.prefersReducedMotion()) {
          callback();
          return;
        }

        let hasRun = false;
        const onEnd = (event) => {
          if (event.target !== card) return;
          done();
        };
        const done = () => {
          if (hasRun) return;
          hasRun = true;
          card.removeEventListener('animationend', onEnd);
          if (this.motionFallback) {
            clearTimeout(this.motionFallback);
            this.motionFallback = null;
          }
          callback();
        };

        card.addEventListener('animationend', onEnd);
        this.motionFallback = setTimeout(done, this.getMotionDuration() + 120);
      }

      getMotionDuration() {
        const duration = getComputedStyle(this).getPropertyValue('--team-directory-motion-duration').trim();

        if (duration.endsWith('ms')) return Number.parseFloat(duration) || 360;
        if (duration.endsWith('s')) return (Number.parseFloat(duration) || 0.36) * 1000;

        return 360;
      }

      prefersReducedMotion() {
        return this.reducedMotionQuery.matches;
      }

      scrollDetailIntoView() {
        this.smoothScrollTo(this.getNearestScrollY(this.detailShell));
      }

      scrollToTrigger(trigger) {
        if (!trigger) return;

        this.smoothScrollTo(this.getNearestScrollY(trigger));
      }

      getNearestScrollY(element) {
        const rect = element.getBoundingClientRect();
        const headerOffset = this.getScrollMarginTop(element);
        const viewportTop = headerOffset;
        const viewportBottom = window.innerHeight;

        if (rect.top < viewportTop) {
          return window.scrollY + rect.top - headerOffset;
        }

        if (rect.bottom > viewportBottom) {
          return window.scrollY + rect.bottom - viewportBottom + 24;
        }

        return window.scrollY;
      }

      getScrollMarginTop(element) {
        const scrollMarginTop = getComputedStyle(element).scrollMarginTop;
        const parsed = Number.parseFloat(scrollMarginTop);

        return Number.isFinite(parsed) ? parsed : 0;
      }

      smoothScrollTo(targetY) {
        if (this.prefersReducedMotion()) {
          window.scrollTo({ top: targetY, behavior: 'auto' });
          return;
        }

        if (this.scrollFrame) {
          cancelAnimationFrame(this.scrollFrame);
        }

        const startY = window.scrollY;
        const distance = targetY - startY;

        if (Math.abs(distance) < 1) return;

        const duration = Math.max(this.getMotionDuration() * 2.1, 800);
        const startTime = performance.now();

        const step = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easedProgress = 1 - Math.pow(1 - progress, 3);

          window.scrollTo(0, startY + distance * easedProgress);

          if (progress < 1) {
            this.scrollFrame = requestAnimationFrame(step);
          } else {
            this.scrollFrame = null;
          }
        };

        this.scrollFrame = requestAnimationFrame(step);
      }
    }
  );
}
